/**
 * Scope Model
 *
 * Shared backbone that drives all three surfaces:
 *   1. Site-visit mode — shows scope awareness inline
 *   2. PDF leave-behind — renders included vs excluded
 *   3. Customer portal — replays agreed scope
 *
 * Three stacks:
 *   - autoScope:     required items derived from the chosen target system
 *   - selectedScope:  optional items the customer chose to include
 *   - excludedScope:  items explicitly excluded (with trade-off warnings)
 */

import type { ScopeItem, ScopeItemStatus } from './types';

// ── Scope snapshot for persistence ──────────────────────────────────

export interface ScopeSnapshot {
  targetSystemId: string;
  targetSystemLabel: string;
  autoScope: ScopeItem[];
  selectedScope: ScopeItem[];
  excludedScope: ScopeItem[];
}

// ── Auto-scope derivation ───────────────────────────────────────────

export function deriveAutoScope(targetSystemId: string): ScopeItem[] {
  const items: ScopeItem[] = [];

  // Unvented cylinder requirements
  if (targetSystemId === 'system-unvented') {
    items.push(
      { id: 'auto-sealed', label: 'Sealed system conversion', description: 'Required for unvented cylinder operation', required: true, status: 'included', notes: '' },
      { id: 'auto-splan', label: 'S-plan valve arrangement', description: 'Required for unvented DHW safety (independent zone isolation)', required: true, status: 'included', notes: '' },
      { id: 'auto-g3-safety', label: 'Unvented safety pack (G3)', description: 'T&P valve, expansion vessel, tundish — building regulations requirement', required: true, status: 'included', notes: '' },
    );
  }

  // Heat pump requirements
  if (targetSystemId === 'heat-pump' || targetSystemId === 'hybrid') {
    items.push(
      { id: 'auto-weathercomp', label: 'Weather compensation controls', description: 'Required for heat pump commissioning (MCS requirement)', required: true, status: 'included', notes: '' },
      { id: 'auto-lowtemp-commission', label: 'Low-temp commissioning baseline', description: 'Flow temp ≤55 °C target, emitter validation', required: true, status: 'included', notes: '' },
      { id: 'auto-sealed-hp', label: 'Sealed system', description: 'Heat pumps require sealed pressurised systems', required: true, status: 'included', notes: '' },
    );
  }

  // Regular/system boiler — sealed conversion (where appropriate)
  if (targetSystemId === 'system-vented') {
    items.push(
      { id: 'auto-sealed-sys', label: 'Sealed system (recommended)', description: 'Modern system boilers expect sealed operation', required: true, status: 'included', notes: '' },
    );
  }

  return items;
}

// ── Spec level types ────────────────────────────────────────────────

/**
 * Spec level for system options:
 * - 'complete': Full spec with all optional items included (default for Options A and B)
 * - 'a-minus': Reduced spec for Option A with optional items removed
 * - 'b-minus': Reduced spec for Option B with optional items removed
 */
export type SpecLevel = 'complete' | 'a-minus' | 'b-minus';

/**
 * Check if an item can be removed for the given target system.
 * Essential items (from autoScope) cannot be removed.
 */
export function canRemoveItem(itemId: string, targetSystemId: string): boolean {
  const autoItems = deriveAutoScope(targetSystemId);
  return !autoItems.some(item => item.id === itemId);
}

// ── Default optional items ──────────────────────────────────────────

/**
 * Get default optional items based on the spec level.
 * 
 * @param specLevel - 'complete' includes all optional items (default for Options A/B),
 *                    'a-minus' or 'b-minus' excludes all optional items for reduced spec
 */
export function getDefaultOptionalItems(specLevel: SpecLevel = 'complete'): ScopeItem[] {
  const baseStatus: ScopeItemStatus = specLevel === 'complete' ? 'included' : 'excluded';
  
  return [
    {
      id: 'opt-28mm-primaries',
      label: '28 mm primaries',
      description: 'Upsize primary flow/return from 22 mm to 28 mm',
      required: false,
      status: baseStatus,
      notes: '',
      tradeOffWarning: 'May limit heat pump performance if flow-restricted at primaries',
    },
    {
      id: 'opt-controls-upgrade',
      label: 'Control upgrade tier',
      description: 'Weather comp / load comp controls (load compensating thermostat)',
      required: false,
      status: baseStatus,
      notes: '',
    },
    {
      id: 'opt-additional-zoning',
      label: 'Additional zoning',
      description: 'Per-room or per-zone TRVs / actuators / zone valves',
      required: false,
      status: baseStatus,
      notes: '',
    },
    {
      id: 'opt-cylinder-replacement',
      label: 'Cylinder replacement',
      description: 'Replace high-loss cylinder with modern well-insulated unit',
      required: false,
      status: baseStatus,
      notes: '',
      tradeOffWarning: 'Standing losses will remain above optimal if existing cylinder is retained',
    },
    {
      id: 'opt-pipe-insulation',
      label: 'Pipe insulation',
      description: 'Insulate accessible primary and DHW distribution runs',
      required: false,
      status: baseStatus,
      notes: '',
    },
    {
      id: 'opt-dhw-distribution',
      label: 'DHW distribution improvements',
      description: 'Reduce dead legs, improve pipe routing, insulate hot runs',
      required: false,
      status: baseStatus,
      notes: '',
    },
    {
      id: 'opt-powerflush',
      label: 'Power flush + magnetic filter',
      description: 'Remove magnetite sludge and install ongoing filtration',
      required: false,
      status: baseStatus,
      notes: '',
      tradeOffWarning: 'Existing contamination will continue to degrade boiler and emitter performance',
    },
    {
      id: 'opt-trvs',
      label: 'TRV upgrade',
      description: 'Replace old TRVs with modern thermostatic heads',
      required: false,
      status: baseStatus,
      notes: '',
    },
  ];
}

// ── Scope manipulation ──────────────────────────────────────────────

export function toggleOptionalItem(items: ScopeItem[], id: string, targetSystemId?: string): ScopeItem[] {
  return items.map(item => {
    if (item.id !== id) return item;
    
    // Check if this item is essential for the target system and cannot be removed
    if (targetSystemId && !canRemoveItem(item.id, targetSystemId)) {
      console.warn(`Cannot remove essential item ${item.id} for system type ${targetSystemId}`);
      return item; // Return unchanged
    }
    
    const newStatus: ScopeItemStatus = item.status === 'included' ? 'excluded' : 'included';
    return { ...item, status: newStatus };
  });
}

export function buildScopeSnapshot(
  targetSystemId: string,
  targetSystemLabel: string,
  optionalItems: ScopeItem[],
): ScopeSnapshot {
  const autoScope = deriveAutoScope(targetSystemId);
  const selectedScope = optionalItems.filter(i => i.status === 'included');
  const excludedScope = optionalItems.filter(i => i.status === 'excluded');
  return { targetSystemId, targetSystemLabel, autoScope, selectedScope, excludedScope };
}

/** Does the current scope meet "highest standard" or is it compromised? */
export function isScopeCompromised(excludedScope: ScopeItem[]): boolean {
  return excludedScope.some(item => !!item.tradeOffWarning);
}

/** Human-readable list of trade-off warnings for excluded items */
export function getTradeOffWarnings(excludedScope: ScopeItem[]): string[] {
  return excludedScope
    .filter(item => !!item.tradeOffWarning)
    .map(item => `${item.label}: ${item.tradeOffWarning}`);
}
